import {Component, OnInit} from '@angular/core';
import {Actions, ofActionErrored, ofActionSuccessful, Select, Store} from '@ngxs/store';
import {VehicleState} from '@shared/state/vehicle/vehicle.state';
import {Observable, Subject} from 'rxjs';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastService} from '@app/service/util/toast.service';
import {NzModalRef} from 'ng-zorro-antd/modal';
import {GetMakes, GetModelsData} from '@shared/state/vehicle/vehicle.actions';
import {take, takeUntil} from 'rxjs/operators';
import {SimulatePrice} from '@shared/state/ml/ml.actions';
import {YEAR_LIST} from '@shared/static-data';

@Component({
  selector: 'app-simulate-car',
  templateUrl: './simulate-car.component.html',
  styleUrls: ['./simulate-car.component.scss']
})
export class SimulateCarComponent implements OnInit {
  @Select(VehicleState.getModels) vehicleModels$: Observable<any>;
  submitted: boolean;

  messages: string[];
  simulateForm: FormGroup;

  makeList: any[];
  modelList: any[];
  modelTypes: string[] = ['XGB', 'RF', 'LR'];
  yearList: number[] = [];

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(private fb: FormBuilder, private store: Store, private toastService: ToastService,
              private actions: Actions, private modalRef: NzModalRef) {
  }

  ngOnInit() {
    this.messages = [];
    this.submitted = false;

    this.yearList = YEAR_LIST();

    this.simulateForm = this.fb.group({
      carYear: ['', Validators.required],
      carMake: ['', Validators.required],
      carModel: ['', Validators.required],
      mileage: ['', [Validators.required, Validators.min(0), Validators.max(200000)]],
      zipCode: ['', [Validators.required, Validators.min(10000), Validators.max(99999)]],
      modelType: ['XGB', Validators.required]
    });

    this.store.dispatch(new GetMakes());
    this.setupVehicleData();
  }

  get f() {
    return this.simulateForm.controls;
  }

  resetForms() {
    this.simulateForm.reset();
  }

  setupVehicleData() {
    // This will only be triggered when the first call to GetModels happens in the onMakeChange function
    this.actions
      .pipe(
        ofActionSuccessful(GetModelsData),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        let makeValue = this.f.carMake.value;
        let yearValue = this.f.carYear.value;
        // If a make is already assigned, and it has models, assign to the model list
        if (makeValue && yearValue) {
          this.modelList = this.setModelList(this.f.carYear.value, makeValue);
        } else if (!yearValue) {
          this.toastService.error('You need to select the year before selecting the model');
        }
      });
  }

  onCarYearChange(value: number) {
    if (!value) {
      this.f.carMake.reset();
      this.f.carModel.reset();
      return this.f.carYear.setErrors({
        message: `Provide a year for the vehicle`
      });
    }

    // Since a valid current year was provided, enable the make field
    this.makeList = this.setMakeList(value);
    this.f.carMake.reset();
    this.f.carModel.reset();
  }

  setMakeList(year: number) {
    const _makeList = this.store.selectSnapshot(VehicleState.getMakes);
    const finalResult = [];

    // Loop through all the makes, only return makes that have all the years in the list
    for (const makeData of _makeList) {
      const makeYears = makeData.years;
      const hasAllYears = makeYears && makeYears.includes(year);
      if (!hasAllYears) {
        continue;
      }
      finalResult.push(makeData);
    }
    return finalResult;
  }

  setModelList(year: number, make) {
    const _modelList = this.store.selectSnapshot(VehicleState.getModels)[make];
    const finalResult = [];

    // Loop through all the makes, only return makes that have all the years in the list
    for (const modelData of _modelList) {
      const years = modelData.years;
      const hasAllYears = years && years.includes(year);
      if (!hasAllYears) {
        continue;
      }
      finalResult.push(modelData);
    }
    return finalResult;
  }

  onCarMakeChange(value: string) {
    if (!value) {
      return;
    }
    this.store.dispatch(new GetModelsData({make: value}));

    this.f.carModel.reset(); // Changing the model should remove previous model data
  }

  simulateCar(): any {
    this.messages = [];
    this.submitted = true;

    // stop here if form is invalid
    if (this.simulateForm.invalid) {
      return;
    }

    const data = this.simulateForm.value;
    this.store.dispatch(new SimulatePrice(data));

    this.actions
      .pipe(
        ofActionSuccessful(SimulatePrice),
        take(1)
      )
      .subscribe(() => {
        this.submitted = false;
        this.resetForms();
        this.modalRef.close();
      });
    this.actions
      .pipe(
        ofActionErrored(SimulatePrice),
        take(1)
      )
      .subscribe(() => {
        this.submitted = false;
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
